import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

const PROGRAMS = [
  ["kickboxing", "Kickboxing"], ["muay_thai", "Muay Thaï"], ["jiu_jitsu", "Jiu-jitsu"],
  ["jeunesse", "Programme jeunesse"], ["powerkick_her", "Powerkick Her"],
  ["famille", "Programme familial"], ["conseil", "J'aimerais être conseillé"],
];
const GOALS = [
  ["confiance", "Confiance en soi"], ["discipline", "Discipline"], ["forme", "Remise en forme"],
  ["autodefense", "Autodéfense"], ["performance", "Performance / compétition"],
  ["enfant", "Encadrement pour mon enfant"], ["famille", "Activité familiale"], ["autre", "Autre"],
];
const FOR_WHO = [["moi", "Moi-même"], ["enfant", "Mon enfant"], ["enfants", "Mes enfants"], ["famille", "Ma famille"], ["autre", "Autre"]];
const INTENT = [["decouverte", "Une activité à découvrir"], ["programme", "Un programme structuré avec progression"], ["accompagnement", "Un accompagnement sérieux vers un objectif précis"], ["incertain", "Je ne suis pas encore certain"]];
const METHOD = [["oui", "Oui, absolument"], ["probablement", "Probablement, si c'est le bon cadre"], ["plus_info", "J'aimerais en savoir plus"], ["non", "Non"]];
const FREQ = [["1", "1 fois"], ["2", "2 fois"], ["3", "3 fois"], ["4+", "4 fois ou plus"]];
const DECISION = [["oui", "Oui"], ["partagee", "Décision partagée"], ["non", "Non"]];
const AVAIL = [["fin_apm", "Fin d'après-midi (semaine)"], ["soiree", "Soirées (semaine)"], ["samedi", "Samedi"], ["variable", "Horaire variable"]];
const INVEST = [["oui", "Oui"], ["peut_etre", "Peut-être"], ["non", "Pas pour le moment"]];
const PRIORITY = [["encadrement", "La qualité de l'encadrement"], ["resultats", "Les résultats"], ["structure", "La structure"], ["ambiance", "L'ambiance"], ["flexibilite", "La flexibilité"], ["prix", "Le prix"]];

const TOTAL = 6;

// Capture la source de campagne depuis l'URL : ?utm=fb_juin (ou utm_campaign / utm_source)
function readCampaign() {
  try {
    const p = new URLSearchParams(window.location.search);
    const raw = p.get("utm") || p.get("utm_campaign") || p.get("utm_source") || "";
    const medium = p.get("utm_medium");
    return medium && raw ? `${raw} (${medium})` : raw;
  } catch { return ""; }
}

function Cards({ options, value, onChange, testid }) {
  return (
    <div className="grid sm:grid-cols-2 gap-2.5" data-testid={testid}>
      {options.map(([v, l]) => (
        <button key={v} type="button" onClick={() => onChange(v)}
          data-testid={`${testid}-${v}`}
          className={`text-left px-4 py-3 rounded-xl border transition-all text-sm ${value === v
            ? "border-amber-500 bg-amber-500/10 text-amber-200 font-semibold shadow-[0_0_16px_rgba(245,158,11,0.12)]"
            : "border-white/10 bg-white/[0.02] text-zinc-300 hover:border-white/25"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block mb-4">
    <span className="text-xs uppercase tracking-widest text-zinc-500 block mb-2">{label}</span>
    {children}
  </label>
);
const inputCls = "w-full rounded-xl bg-zinc-900 border border-white/10 px-4 py-3 text-sm text-zinc-100 focus:border-amber-500 outline-none";

export default function AdmissionPublic() {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [f, setF] = useState({
    nom: "", courriel: "", telephone: "", demande_pour: "", programme_interet: "", objectif_principal: "",
    intention: "", pourquoi_maintenant: "", resultat_recherche: "", deja_essaye: "", pret_a_suivre_methode: "",
    frequence_possible: "", engagement_sur_10: "", decideur_final: "", disponibilites: "", pret_a_investir: "",
    priorite_decision: "", contraintes: "", source_campagne: readCampaign(),
  });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const setInput = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const canNext = () => {
    if (step === 0) return f.nom.trim() && /\S+@\S+/.test(f.courriel) && f.telephone.trim() && f.demande_pour;
    if (step === 1) return f.programme_interet && f.objectif_principal;
    if (step === 2) return f.pourquoi_maintenant.trim().length > 3;
    if (step === 3) return f.intention && f.pret_a_suivre_methode && f.frequence_possible && f.decideur_final;
    if (step === 4) return f.disponibilites && f.pret_a_investir && f.priorite_decision;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...f, engagement_sur_10: f.engagement_sur_10 ? Number(f.engagement_sur_10) : null };
      const { data } = await api.post("/admission/submit", payload);
      setResult(data);
      setStep(TOTAL);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Une erreur est survenue. Réessaie.");
    } finally { setBusy(false); }
  };

  const next = () => { if (!canNext()) { toast.error("Complète les champs requis pour continuer."); return; } step === TOTAL - 1 ? submit() : setStep((s) => s + 1); };

  const pct = Math.round((Math.min(step, TOTAL) / TOTAL) * 100);
  const isParent = ["enfant", "enfants"].includes(f.demande_pour);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="admission-public">
      <div className="max-w-2xl mx-auto px-5 py-10">
        <div className="text-center mb-8">
          <div className="text-[11px] uppercase tracking-[0.28em] text-amber-500 font-bold">Institut Impact Élite Martial × Kaizen System</div>
          <h1 className="text-3xl sm:text-4xl font-black mt-2">Admission & qualification</h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-lg mx-auto">
            Nous n'accompagnons pas que des inscriptions — nous accompagnons les bonnes personnes.
            Ce court parcours nous permet de vérifier si nous sommes le bon fit et de t'orienter vers le programme adapté.
          </p>
        </div>

        {step < TOTAL && (
          <div className="mb-6">
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-500 to-red-500 transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <div className="text-[11px] text-zinc-500 mt-1.5 text-right">Étape {step + 1} sur {TOTAL}</div>
          </div>
        )}

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          {step === 0 && (
            <div>
              <h2 className="text-lg font-bold mb-4">Faisons connaissance</h2>
              <Field label="Nom complet *"><input data-testid="adm-nom" className={inputCls} value={f.nom} onChange={setInput("nom")} placeholder="Prénom Nom" /></Field>
              <Field label="Courriel *"><input data-testid="adm-courriel" type="email" className={inputCls} value={f.courriel} onChange={setInput("courriel")} placeholder="ton@courriel.com" /></Field>
              <Field label="Téléphone *"><input data-testid="adm-telephone" type="tel" className={inputCls} value={f.telephone} onChange={setInput("telephone")} placeholder="819-000-0000" /></Field>
              <Field label="Cette demande est pour *"><Cards options={FOR_WHO} value={f.demande_pour} onChange={set("demande_pour")} testid="adm-forwho" /></Field>
            </div>
          )}
          {step === 1 && (
            <div>
              <h2 className="text-lg font-bold mb-4">Quel programme t'attire le plus ?</h2>
              <Cards options={PROGRAMS} value={f.programme_interet} onChange={set("programme_interet")} testid="adm-program" />
              <div className="mt-5"><Field label="Ton objectif principal *"><Cards options={GOALS} value={f.objectif_principal} onChange={set("objectif_principal")} testid="adm-goal" /></Field></div>
            </div>
          )}
          {step === 2 && (
            <div>
              <h2 className="text-lg font-bold mb-4">Ta motivation</h2>
              <Field label="Pourquoi veux-tu commencer maintenant ? *"><textarea data-testid="adm-why" className={`${inputCls} min-h-[90px]`} value={f.pourquoi_maintenant} onChange={setInput("pourquoi_maintenant")} /></Field>
              <Field label="Qu'aimerais-tu avoir accompli dans les 3-6 prochains mois ?"><textarea data-testid="adm-outcome" className={`${inputCls} min-h-[70px]`} value={f.resultat_recherche} onChange={setInput("resultat_recherche")} /></Field>
              <Field label="Qu'as-tu déjà essayé auparavant ?"><input data-testid="adm-tried" className={inputCls} value={f.deja_essaye} onChange={setInput("deja_essaye")} /></Field>
            </div>
          )}
          {step === 3 && (
            <div>
              <h2 className="text-lg font-bold mb-4">Ton engagement</h2>
              <Field label="Aujourd'hui, tu recherches surtout *"><Cards options={INTENT} value={f.intention} onChange={set("intention")} testid="adm-intent" /></Field>
              <Field label="Prêt à suivre une méthode structurée (étapes, constance, encadrement) ? *"><Cards options={METHOD} value={f.pret_a_suivre_methode} onChange={set("pret_a_suivre_methode")} testid="adm-method" /></Field>
              <Field label="Combien de fois par semaine, réalistement ? *"><Cards options={FREQ} value={f.frequence_possible} onChange={set("frequence_possible")} testid="adm-freq" /></Field>
              <Field label="Ton niveau d'engagement sur 10">
                <input data-testid="adm-engagement" type="range" min="1" max="10" value={f.engagement_sur_10 || 5} onChange={setInput("engagement_sur_10")} className="w-full accent-amber-500" />
                <div className="text-center text-amber-400 font-bold mt-1">{f.engagement_sur_10 || 5}/10</div>
              </Field>
              <Field label="Es-tu la personne qui prend la décision finale ? *"><Cards options={DECISION} value={f.decideur_final} onChange={set("decideur_final")} testid="adm-decision" /></Field>
            </div>
          )}
          {step === 4 && (
            <div>
              <h2 className="text-lg font-bold mb-4">Ta réalité concrète</h2>
              <Field label="Tes disponibilités habituelles *"><Cards options={AVAIL} value={f.disponibilites} onChange={set("disponibilites")} testid="adm-avail" /></Field>
              <Field label="Si le bon programme te convient, prêt à investir dans un cadre sérieux ? *"><Cards options={INVEST} value={f.pret_a_investir} onChange={set("pret_a_investir")} testid="adm-invest" /></Field>
              <Field label="Qu'est-ce qui compte le plus dans ton choix d'école ? *"><Cards options={PRIORITY} value={f.priorite_decision} onChange={set("priorite_decision")} testid="adm-priority" /></Field>
              <Field label="Des contraintes (transport, horaire, famille) à considérer ?"><input data-testid="adm-constraints" className={inputCls} value={f.contraintes} onChange={setInput("contraintes")} /></Field>
            </div>
          )}
          {step === 5 && (
            <div>
              <h2 className="text-lg font-bold mb-4">Dernière étape</h2>
              <p className="text-sm text-zinc-400 mb-4">Vérifie tes informations, puis envoie ta fiche. Nous t'orienterons vers le programme le plus adapté.</p>
              <div className="rounded-xl bg-zinc-900 border border-white/10 p-4 text-sm space-y-1.5">
                <div><span className="text-zinc-500">Nom :</span> {f.nom}</div>
                <div><span className="text-zinc-500">Contact :</span> {f.courriel} · {f.telephone}</div>
                <div><span className="text-zinc-500">Programme :</span> {(PROGRAMS.find((p) => p[0] === f.programme_interet) || [])[1]}</div>
                <div><span className="text-zinc-500">Objectif :</span> {(GOALS.find((g) => g[0] === f.objectif_principal) || [])[1]}</div>
              </div>
            </div>
          )}
          {step === TOTAL && result && (
            <div className="text-center py-4" data-testid="adm-result">
              <div className="text-5xl mb-3">🥋</div>
              {result.niveau_qualification === "faible" ? (
                <>
                  <h2 className="text-xl font-bold text-amber-300 mb-2">Merci pour ta démarche</h2>
                  <p className="text-sm text-zinc-400 max-w-md mx-auto">À ce stade, il est possible que notre structure ne soit pas l'option la plus adaptée à ta situation actuelle. Comme nous privilégions un bon alignement, nous préférons être transparents dès le départ. Nous te recontacterons avec la meilleure suite.</p>
                </>
              ) : (
                <>
                  <h2 className="text-xl font-bold text-emerald-300 mb-2">Excellent — il y a un bon fit !</h2>
                  <p className="text-sm text-zinc-400 max-w-md mx-auto mb-4">D'après tes réponses, ton profil correspond bien à notre structure. La prochaine étape est de t'orienter vers le programme le plus adapté pour bâtir un parcours clair et engagé.</p>
                </>
              )}
              <div className="mt-5 inline-flex flex-col gap-2 rounded-2xl border border-white/10 bg-white/[0.02] px-6 py-4">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Programme recommandé</div>
                <div className="text-lg font-bold text-amber-300" data-testid="adm-reco">{result.programme_recommande}</div>
              </div>
              <p className="text-xs text-zinc-600 mt-6">Un membre de l'équipe te contactera sous peu. Merci !</p>
            </div>
          )}

          {step < TOTAL && (isParent && step === 2) && (
            <p className="text-[11px] text-zinc-500 mt-3">💡 Astuce : précise l'âge et les besoins de ton enfant (confiance, discipline, concentration) dans le champ ci-dessus.</p>
          )}

          {step < TOTAL && (
            <div className="flex items-center justify-between mt-6">
              <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                data-testid="adm-back" className="text-sm text-zinc-400 hover:text-zinc-200 disabled:opacity-30">← Retour</button>
              <button type="button" onClick={next} disabled={busy} data-testid="adm-next"
                className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-red-500 text-zinc-950 font-bold text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
                {busy ? "Envoi…" : step === TOTAL - 1 ? "Envoyer ma fiche" : "Continuer"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
